import { makeStyles, tokens } from '@fluentui/react-components';
import { AuthStatus, useAuth, useT } from '@powerportalspro/react';
import { Calendar, NewAppointmentCalendarButton } from '@powerportalspro/react-calendar';
import type { MultiTableViewDefinition } from '@powerportalspro/core';
import { useMemo } from 'react';
import { DemoShowcaseHeading, DemoShowcaseLayout } from './DemoShowcaseLayout';

// Every appointment of the demo's sample data, a month at a time, beside what to try in it and what
// decides which appointments a visitor sees. The calendar reads each range as it is shown, narrowed
// by the view picked and the search, and keeps its place in the URL. A signed-in visitor may add
// appointments of their own, which only they see, and the calendar opens on them.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.CalendarShowcase.CalendarShowcaseMainDemo';

// Dataverse's own "All Appointments" view, which every environment has under this id.
const ALL_APPOINTMENTS_VIEW_ID = '5b5f75f6-b585-4212-952a-b4cbb64e2627';
const MY_APPOINTMENTS_VIEW_ID = '1f0c8a52-6d3e-4b7a-9c21-5e8f4a7b3d03';
// Offered always, beside the views defined below, whichever the calendar opens on.
const ALL_APPOINTMENTS_VIEW_IDS = [ALL_APPOINTMENTS_VIEW_ID];

const useStyles = makeStyles({
  list: {
    margin: 0,
    paddingInlineStart: tokens.spacingHorizontalXL,
  },
});

// The strings are HTML, written once for both stacks.
function Item({ text }: { text: string }) {
  return <li dangerouslySetInnerHTML={{ __html: text }} />;
}

export function CalendarShowcaseMainSample() {
  const styles = useStyles();
  const t = useT();
  const auth = useAuth();
  const userId = auth.status === AuthStatus.Authenticated ? (auth.user.userId ?? null) : null;

  // Views defined here rather than in Dataverse: the meetings held in Teams, the occurrences of
  // recurring appointments, the appointments with the tasks and phone calls beside them and, for a
  // signed-in visitor, the appointments they added, which the calendar then opens on.
  const views = useMemo<MultiTableViewDefinition[]>(
    () => [
      {
        id: '1f0c8a52-6d3e-4b7a-9c21-5e8f4a7b3d01',
        displayName: t(`${STRINGS_BASE}.view-teams`),
        tables: [{ table: 'appointment', fetchXml: `
          <fetch>
            <entity name="appointment">
              <attribute name="subject" />
              <attribute name="location" />
              <filter>
                <condition attribute="location" operator="eq" value="Microsoft Teams" />
              </filter>
            </entity>
          </fetch>` }],
      },
      {
        id: '1f0c8a52-6d3e-4b7a-9c21-5e8f4a7b3d02',
        displayName: t(`${STRINGS_BASE}.view-recurring`),
        tables: [{ table: 'appointment', fetchXml: `
          <fetch>
            <entity name="appointment">
              <attribute name="subject" />
              <attribute name="location" />
              <filter>
                <condition attribute="seriesid" operator="not-null" />
              </filter>
            </entity>
          </fetch>` }],
      },
      {
        id: '1f0c8a52-6d3e-4b7a-9c21-5e8f4a7b3d04',
        displayName: t(`${STRINGS_BASE}.view-activities`),
        tables: [{ table: 'appointment' }, { table: 'task' }, { table: 'phonecall' }],
      },
      ...(userId
        ? [
            {
              id: MY_APPOINTMENTS_VIEW_ID,
              displayName: t(`${STRINGS_BASE}.view-mine`),
              tables: [{ table: 'appointment', fetchXml: `
                <fetch>
                  <entity name="appointment">
                    <attribute name="subject" />
                    <attribute name="location" />
                    <filter>
                      <condition attribute="ppp_owningportaluserid" operator="eq" value="${userId}" />
                    </filter>
                  </entity>
                </fetch>` }],
            },
          ]
        : []),
    ],
    [t, userId],
  );

  return (
    <DemoShowcaseLayout
      title={t(`${STRINGS_BASE}.try-title`)}
      tips={
        <>
          <ul className={styles.list}>
            <Item text={t(`${STRINGS_BASE}.try-views`)} />
            <Item text={t(`${STRINGS_BASE}.try-more`)} />
            <Item text={t(`${STRINGS_BASE}.try-years`)} />
            <Item text={t(`${STRINGS_BASE}.try-series`)} />
            <Item text={t(`${STRINGS_BASE}.try-filter`)} />
            <Item text={t(`${STRINGS_BASE}.try-tables`)} />
            <Item text={t(`${STRINGS_BASE}.try-saved-views`)} />
            <Item text={t(`${STRINGS_BASE}.try-link`)} />
            <Item text={t(`${STRINGS_BASE}.try-add`)} />
          </ul>

          <DemoShowcaseHeading>{t(`${STRINGS_BASE}.rules-title`)}</DemoShowcaseHeading>
          <ul className={styles.list}>
            <Item text={t(`${STRINGS_BASE}.rule-access`)} />
            <Item text={t(`${STRINGS_BASE}.rule-prefix`)} />
            <Item text={t(`${STRINGS_BASE}.rule-ranges`)} />
          </ul>
        </>
      }
    >
      {/* Drawn once the user is known, so the calendar doesn't read every appointment first and
          then the signed-in visitor's own. */}
      {auth.status !== AuthStatus.Loading && (
        <Calendar
          defaultLayout="month"
          height="760px"
          viewIds={ALL_APPOINTMENTS_VIEW_IDS}
          defaultViewId={userId ? MY_APPOINTMENTS_VIEW_ID : ALL_APPOINTMENTS_VIEW_ID}
          customViewDefinitions={views}
          allowFiltering
          allowSavedViews
          savedViewsKey="calendar-showcase"
          persistedStateQueryParameter="calendarState"
          editable
        >
          <NewAppointmentCalendarButton />
        </Calendar>
      )}
    </DemoShowcaseLayout>
  );
}
